import React from 'react';
import i18next from 'i18next';
import { Popover, Space, Table, TableProps, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';

import useCustomTranslation from '@/hooks/useCustomTranslation';

import IInventoryProduct from '@/interfaces/IInventoryProduct';
import formatQty from '@/shared/helpers/formatQty';

interface Props {
  data: IInventoryProduct[];
  loading: boolean,
  pagination: TableProps<IInventoryProduct>['pagination'];
  rowSelection?: TableProps<IInventoryProduct>['rowSelection'];
  expandable?: TableProps<IInventoryProduct>['expandable'];

  showAvailableAndPending?: boolean;
  renderActions?: React.ReactNode | ((record: IInventoryProduct, index: number) => React.ReactNode);
}

const InventoryProductsList = ({
  data,
  loading,
  showAvailableAndPending = true,
  pagination,
  rowSelection,
  expandable,
  renderActions,
}: Props) => {
  const { t } = useCustomTranslation();
  const lang = i18next.language;

  const columns: ColumnsType<IInventoryProduct> = [
    {
      title: t('g.sku'),
      dataIndex: ['sku'],
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: t('g.product_name'),
      dataIndex: [lang === 'en' ? 'nameEn' : 'nameEs'],
    },
    {
      title: t('g.attributes'),
      dataIndex: 'attributeOptions',
      render: (attributeOptions: IInventoryProduct['attributeOptions']) => {
        if (attributeOptions.length === 0) {
          return (
            <Typography.Text type="secondary">N/A</Typography.Text>
          )
        }

        if (attributeOptions.length === 1) {
          return (
            <Tag>{`${attributeOptions[0].attribute.labelEs}: ${attributeOptions[0].labelEs}`}</Tag>
          );
        }

        if (attributeOptions.length > 1) {
          return (
            <Popover
              content={(
                <Space direction="vertical" style={{ width: '100%'}}>
                  {attributeOptions.map((option) => (
                    <Tag key={option.id}>{`${option.attribute.labelEs}: ${option.labelEs}`}</Tag>
                  ))}
                </Space>
              )}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Tag>{`${attributeOptions[0].attribute.labelEs}: ${attributeOptions[0].labelEs}`}</Tag>
                <Tag>
                  <Typography.Text type="secondary">
                    {`${attributeOptions.length - 1} más`}
                  </Typography.Text>
                </Tag>
              </Space>
            </Popover>
          );
        }

        return null;
      }
    },
    ...showAvailableAndPending ? [{
      title: t('g.available_for_sale'),
      render: (_: IInventoryProduct['forSale'], value: IInventoryProduct) => {
        if (value.pending === undefined || value.forSale === undefined) {
          return (
            <Typography.Text style={{ float: 'right' }}>
              --
            </Typography.Text>
          );
        }
        const availableForSale = formatQty({
          qty: value.forSale - value.pending,
          decimalPoints: value.decimalPoints,
        });
        
        return (
          <Typography.Text style={{ float: 'right' }}>
            {`${availableForSale} ${value.unit}`}
          </Typography.Text>
        );
      },
    },
    {
      title: t('g.not_available_for_sale'),
      dataIndex: ['forSale'],
      render: (_: IInventoryProduct['forSale'], value: IInventoryProduct) => {
        if (value.total === undefined || value.forSale === undefined) {
          return (
            <Typography.Text style={{ float: 'right' }}>
              --
            </Typography.Text>
          );
        }

        const notAvailableForSale = formatQty({
          qty: value.total - value.forSale,
          decimalPoints: value.decimalPoints,
        });

        return (
          <Typography.Text style={{ float: 'right' }}>
            {`${notAvailableForSale} ${value.unit}`}
          </Typography.Text>
        );
      },
    },
    {
      title: t('g.sold'),
      dataIndex: ['pending'],
      render: (_: IInventoryProduct['pending'], value: IInventoryProduct) => {
        if (value.pending === undefined) {
          return (
            <Typography.Text style={{ float: 'right' }}>
              --
            </Typography.Text>
          );
        }
        const pendingTotal = formatQty({
          qty: value.pending,
          decimalPoints: value.decimalPoints,
        });

        return (
          <Typography.Text style={{ float: 'right' }}>
            {`${pendingTotal} ${value.unit}`}
          </Typography.Text>
        );
      },
    }] : [],
    {
      title: t('g.total'),
      dataIndex: ['total'],
      render: (_, value: IInventoryProduct) => {
        if (value.total === undefined) {
          return (
            <Typography.Text style={{ float: 'right' }}>
              --
            </Typography.Text>
          );
        }
        const total = formatQty({
          qty: value.total,
          decimalPoints: value.decimalPoints,
        });

        if (!total) {
          return (
            <Typography.Text style={{ float: 'right' }}>
              --
            </Typography.Text>
          );
        }

        return (
          <Typography.Text style={{ float: 'right' }}>
            {`${total} ${value.unit}`}
          </Typography.Text>
        );
      },
    },
  ];

  if (renderActions) {
    columns.push(
      {
        key: 'actions',
        title: t('g.actions'),
        width: 30,
        align: 'right',
        render: (_, record, index) => {
          if (typeof renderActions === 'function') {
            return renderActions(record, index);
          }
          return renderActions;
        },
      },
    );
  }

  return (
    <Table
      style={{ whiteSpace: 'nowrap' }}
      scroll={{ x: true }}
      rowKey={row => row.productId}
      rowSelection={rowSelection}
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={pagination}
      expandable={expandable}
    />
  );
};


export default InventoryProductsList;

